import { useCallback, useRef } from "react";

/**
 * While `withLockedHoverReset` is running an async animation sequence, `shouldApplyHoverReset`
 * is false so `mouseLeave` can avoid calling `controls.start("normal")` mid-playthrough
 * (e.g. after a sidebar nav pointer-down triggers `startAnimation`).
 */
export function useHoverResetGuard() {
  const lockDepthRef = useRef(0);

  const withLockedHoverReset = useCallback(async (run: () => Promise<void>) => {
    lockDepthRef.current += 1;
    try {
      await run();
    } finally {
      lockDepthRef.current = Math.max(0, lockDepthRef.current - 1);
    }
  }, []);

  const releaseHoverResetLock = useCallback(() => {
    lockDepthRef.current = 0;
  }, []);

  const shouldApplyHoverReset = useCallback(() => lockDepthRef.current === 0, []);

  /** Run a reset only when not in a locked click/hover animation (sidebar row mouse leave). */
  const applyHoverReset = useCallback(
    (reset: () => void | Promise<void>) => {
      if (!shouldApplyHoverReset()) return;
      releaseHoverResetLock();
      void reset();
    },
    [releaseHoverResetLock, shouldApplyHoverReset]
  );

  return {
    withLockedHoverReset,
    releaseHoverResetLock,
    shouldApplyHoverReset,
    applyHoverReset,
  };
}
